import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";

import type { LivenessSession } from "../../components/face-liveness/FaceLivenessCheck";
import {
  activateKioskApi,
  cpfApi,
  identifyApi,
  KioskCandidate,
  KioskError,
  KioskIdentification,
  kioskPunchApi,
  KioskReceipt,
  KioskStatus,
  kioskStatusApi,
  readKioskToken,
  saveKioskToken,
  startKioskLivenessApi,
} from "./kioskApi";
import "./Kiosk.css";

const FaceLivenessCheck = React.lazy(() => import("../../components/face-liveness/FaceLivenessCheck"));

/** Qualquer tela volta para a espera depois disto sem toque (menos a verificação em andamento). */
const IDLE_MS = 30_000;
const RECEIPT_MS = 8_000;
const RETRY_MS = 15_000;
const STATUS_REFRESH_MS = 60_000;

type Step =
  | { kind: "activate" }
  | { kind: "idle" }
  | { kind: "loading"; text: string }
  | { kind: "liveness"; session: LivenessSession }
  | { kind: "result"; identification: KioskIdentification }
  | { kind: "cpf"; attemptId: string; message: string | null }
  | { kind: "choose"; attemptId: string; candidates: KioskCandidate[] }
  | { kind: "confirm"; attemptId: string; employee: KioskCandidate; byCpf: boolean }
  | { kind: "receipt"; receipt: KioskReceipt }
  | { kind: "error"; message: string };

/** UUID v4 da marcação (retentativa segura). `randomUUID` só existe em contexto seguro; o resto monta à mão. */
const newRequestId = (): string => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Quiosque da loja (ADR 0031): celular fixo onde o colaborador marca o ponto
 * pelo rosto. Fora do layout do staff e do portal, em tela cheia, com o token do
 * próprio aparelho. Se o rosto não for reconhecido, os 4 primeiros dígitos do
 * CPF — e a marcação vai para conferência do DP.
 */
export default function KioskPage() {
  const [step, setStep] = useState<Step>(() => (readKioskToken() ? { kind: "idle" } : { kind: "activate" }));
  const [status, setStatus] = useState<KioskStatus | null>(null);
  const [offline, setOffline] = useState(false);
  const [clockOffset, setClockOffset] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const requestId = useRef<string | null>(null);
  const idleTimer = useRef<number | null>(null);

  const deactivate = useCallback(() => {
    saveKioskToken(null);
    setStatus(null);
    setStep({ kind: "activate" });
  }, []);

  /** Erro de qualquer chamada: sem conexão, aparelho revogado, ou mensagem do servidor. */
  const fail = useCallback(
    (error: unknown) => {
      const e = error instanceof KioskError ? error : new KioskError("Algo deu errado. Tente de novo.", null, 0);
      if (e.unauthorized) return deactivate();
      if (e.offline) {
        setOffline(true);
        return setStep({ kind: "idle" });
      }
      setStep({ kind: "error", message: e.message });
    },
    [deactivate],
  );

  const refreshStatus = useCallback(async () => {
    if (!readKioskToken()) return;
    try {
      const s = await kioskStatusApi();
      setStatus(s);
      setOffline(false);
      setClockOffset(new Date(s.serverNow).getTime() - Date.now());
    } catch (error) {
      if (error instanceof KioskError && error.unauthorized) deactivate();
      else setOffline(true);
    }
  }, [deactivate]);

  // Relógio, status periódico e nova tentativa quando está sem conexão.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);
  useEffect(() => {
    if (step.kind === "activate") return;
    refreshStatus();
    const every = window.setInterval(refreshStatus, offline ? RETRY_MS : STATUS_REFRESH_MS);
    return () => window.clearInterval(every);
  }, [step.kind, offline, refreshStatus]);

  // Volta para a espera sem toque (a verificação em andamento e a ativação não contam).
  const resetIdle = useCallback(() => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    if (step.kind === "idle" || step.kind === "activate" || step.kind === "liveness" || step.kind === "loading") return;
    const wait = step.kind === "receipt" ? RECEIPT_MS : IDLE_MS;
    idleTimer.current = window.setTimeout(() => setStep({ kind: "idle" }), wait);
  }, [step.kind]);
  useEffect(() => {
    resetIdle();
    return () => {
      if (idleTimer.current) window.clearTimeout(idleTimer.current);
    };
  }, [resetIdle]);

  const start = async () => {
    requestId.current = null;
    setStep({ kind: "loading", text: "Preparando a câmera..." });
    try {
      setStep({ kind: "liveness", session: await startKioskLivenessApi() });
    } catch (error) {
      fail(error);
    }
  };

  const identify = async (sessionId: string) => {
    setStep({ kind: "loading", text: "Identificando..." });
    try {
      const identification = await identifyApi(sessionId);
      if (identification.outcome === "MATCH" && identification.employee) {
        setStep({ kind: "confirm", attemptId: identification.attemptId, employee: identification.employee, byCpf: false });
      } else {
        setStep({ kind: "result", identification });
      }
    } catch (error) {
      fail(error);
    }
  };

  const searchCpf = async (attemptId: string, digits: string) => {
    setStep({ kind: "loading", text: "Buscando..." });
    try {
      const { candidates, triesLeft } = await cpfApi(attemptId, digits);
      if (candidates.length === 1) return setStep({ kind: "confirm", attemptId, employee: candidates[0], byCpf: true });
      if (candidates.length > 1) return setStep({ kind: "choose", attemptId, candidates });
      setStep({
        kind: "cpf",
        attemptId,
        message: triesLeft > 0 ? `CPF não encontrado. Confira os números (mais ${triesLeft} tentativa${triesLeft > 1 ? "s" : ""}).` : "CPF não encontrado.",
      });
    } catch (error) {
      fail(error);
    }
  };

  const punch = async (attemptId: string, employeeId: string) => {
    requestId.current = requestId.current ?? newRequestId();
    setStep({ kind: "loading", text: "Marcando o ponto..." });
    try {
      const receipt = await kioskPunchApi({ attemptId, employeeId, clientRequestId: requestId.current, clientTimestamp: new Date().toISOString() });
      requestId.current = null;
      setStep({ kind: "receipt", receipt });
    } catch (error) {
      fail(error);
    }
  };

  const serverNow = new Date(now + clockOffset);
  const clock = serverNow.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
  const date = serverNow.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", timeZone: "America/Sao_Paulo" });
  const unavailable = status && (!status.timekeepingEnabled || !status.recognitionAvailable);

  return (
    <div className="kiosk" onPointerDown={resetIdle}>
      {step.kind !== "activate" ? (
        <header className="kiosk__header">
          <span className="kiosk__store">{status?.store.name ?? ""}</span>
          <span className="kiosk__clock">{clock}</span>
        </header>
      ) : null}

      <main className="kiosk__main">
        {step.kind === "activate" ? <ActivateStep onActivated={() => setStep({ kind: "idle" })} /> : null}

        {step.kind === "idle" ? (
          <div className="kiosk__panel">
            <p className="kiosk__date">{date}</p>
            <div className="kiosk__bigClock">{clock}</div>
            {offline ? (
              <p className="kiosk__alert">Sem conexão. Se não voltar, peça a inclusão do ponto ao DP pelo app.</p>
            ) : unavailable ? (
              <p className="kiosk__alert">
                {!status!.timekeepingEnabled ? "O ponto eletrônico ainda não foi liberado." : "O reconhecimento facial está indisponível. Fale com o DP."}
              </p>
            ) : (
              <>
                <button className="kiosk__primary" onClick={start} disabled={!status}>
                  Bater ponto
                </button>
                <p className="kiosk__hint">Olhe para a câmera. A tela vai piscar cores por alguns segundos.</p>
              </>
            )}
          </div>
        ) : null}

        {step.kind === "loading" ? (
          <div className="kiosk__panel">
            <div className="kiosk__spinner" aria-hidden />
            <p className="kiosk__text">{step.text}</p>
          </div>
        ) : null}

        {step.kind === "liveness" ? (
          <div className="kiosk__liveness">
            <Suspense fallback={<p className="kiosk__text">Preparando a câmera...</p>}>
              <FaceLivenessCheck
                session={step.session}
                skipStartScreen
                onComplete={() => identify(step.session.sessionId)}
                onCancel={() => setStep({ kind: "idle" })}
                onError={(message) => setStep({ kind: "error", message })}
              />
            </Suspense>
          </div>
        ) : null}

        {step.kind === "result" ? (
          <div className="kiosk__panel">
            <p className="kiosk__title">{step.identification.outcome === "NOT_LIVE" ? "Não conseguimos confirmar o rosto" : "Não reconhecemos seu rosto"}</p>
            <p className="kiosk__text">
              {step.identification.outcome === "NOT_LIVE"
                ? "Fique de frente para a câmera, sem óculos escuros, boné ou máscara, num lugar com boa luz."
                : step.identification.cpfAllowed
                  ? "Tente de novo ou use os 4 primeiros números do seu CPF."
                  : "Tente de novo."}
            </p>
            <div className="kiosk__actions">
              <button className="kiosk__primary" onClick={start}>
                Tentar de novo
              </button>
              {step.identification.cpfAllowed ? (
                <button className="kiosk__secondary" onClick={() => setStep({ kind: "cpf", attemptId: step.identification.attemptId, message: null })}>
                  Usar CPF
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {step.kind === "confirm" ? (
          <div className="kiosk__panel">
            <p className="kiosk__question">Você é</p>
            <p className="kiosk__name">{step.employee.displayName}?</p>
            {step.byCpf ? <p className="kiosk__hint">A marcação pelo CPF vai para conferência do DP.</p> : null}
            <div className="kiosk__actions">
              <button className="kiosk__primary" onClick={() => punch(step.attemptId, step.employee.id)}>
                Sou eu
              </button>
              <button className="kiosk__secondary" onClick={() => setStep({ kind: "cpf", attemptId: step.attemptId, message: null })}>
                Não sou eu
              </button>
            </div>
          </div>
        ) : null}

        {step.kind === "cpf" ? (
          <CpfStep message={step.message} onCancel={() => setStep({ kind: "idle" })} onSubmit={(digits) => searchCpf(step.attemptId, digits)} />
        ) : null}

        {step.kind === "choose" ? (
          <div className="kiosk__panel">
            <p className="kiosk__title">Quem é você?</p>
            <div className="kiosk__choices">
              {step.candidates.map((c) => (
                <button key={c.id} className="kiosk__choice" onClick={() => setStep({ kind: "confirm", attemptId: step.attemptId, employee: c, byCpf: true })}>
                  {c.displayName}
                </button>
              ))}
            </div>
            <button className="kiosk__link" onClick={() => setStep({ kind: "idle" })}>
              Cancelar
            </button>
          </div>
        ) : null}

        {step.kind === "receipt" ? (
          <div className="kiosk__panel kiosk__panel--ok">
            <div className="kiosk__check" aria-hidden>
              ✓
            </div>
            <p className="kiosk__title">{step.receipt.employeeName}</p>
            <p className="kiosk__receiptTime">
              {step.receipt.label} às {step.receipt.time}
            </p>
            <p className="kiosk__hint">
              Comprovante NSR {step.receipt.nsr}
              {step.receipt.identification === "CPF" ? " · vai para conferência do DP" : ""}
            </p>
            <button className="kiosk__secondary" onClick={() => setStep({ kind: "idle" })}>
              Concluir
            </button>
          </div>
        ) : null}

        {step.kind === "error" ? (
          <div className="kiosk__panel">
            <p className="kiosk__title">Não deu certo</p>
            <p className="kiosk__text">{step.message}</p>
            <div className="kiosk__actions">
              <button className="kiosk__primary" onClick={start}>
                Tentar de novo
              </button>
              <button className="kiosk__secondary" onClick={() => setStep({ kind: "idle" })}>
                Voltar ao início
              </button>
            </div>
          </div>
        ) : null}
      </main>

      {status && step.kind !== "activate" ? <footer className="kiosk__footer">{status.device.name}</footer> : null}
    </div>
  );
}

/** Ativação com o código de 8 caracteres gerado no painel do DP. */
function ActivateStep({ onActivated }: { onActivated: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.replace(/[\s-]/g, "").length !== 8) return setError("O código tem 8 letras e números.");
    setBusy(true);
    setError(null);
    try {
      const { token } = await activateKioskApi(code);
      saveKioskToken(token);
      onActivated();
    } catch (err) {
      setError(err instanceof KioskError && err.offline ? "Sem conexão. Confira a internet do aparelho." : (err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="kiosk__panel" onSubmit={submit}>
      <p className="kiosk__title">Ativar o aparelho</p>
      <p className="kiosk__text">Digite o código gerado pelo DP em Cadastros → Aparelhos do quiosque. O código vale 15 minutos.</p>
      <input
        className="kiosk__codeInput"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        maxLength={9}
        autoCapitalize="characters"
        autoComplete="off"
        placeholder="XXXX-XXXX"
        aria-label="Código de ativação"
      />
      {error ? <p className="kiosk__alert">{error}</p> : null}
      <button className="kiosk__primary" type="submit" disabled={busy}>
        {busy ? "Ativando..." : "Ativar"}
      </button>
    </form>
  );
}

/** Teclado numérico dos 4 primeiros dígitos do CPF. */
function CpfStep({ message, onSubmit, onCancel }: { message: string | null; onSubmit: (digits: string) => void; onCancel: () => void }) {
  const [digits, setDigits] = useState("");
  const press = (d: string) => setDigits((v) => (v.length < 4 ? v + d : v));

  return (
    <div className="kiosk__panel">
      <p className="kiosk__title">Os 4 primeiros números do seu CPF</p>
      <div className="kiosk__digits" aria-live="polite">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={`kiosk__digit${digits[i] ? " kiosk__digit--filled" : ""}`}>
            {digits[i] ?? ""}
          </span>
        ))}
      </div>
      {message ? <p className="kiosk__alert">{message}</p> : null}
      <div className="kiosk__keypad">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} className="kiosk__key" onClick={() => press(d)}>
            {d}
          </button>
        ))}
        <button className="kiosk__key kiosk__key--muted" onClick={() => setDigits("")}>
          Limpar
        </button>
        <button className="kiosk__key" onClick={() => press("0")}>
          0
        </button>
        <button className="kiosk__key kiosk__key--muted" onClick={() => setDigits((v) => v.slice(0, -1))} aria-label="Apagar">
          ⌫
        </button>
      </div>
      <div className="kiosk__actions">
        <button className="kiosk__primary" disabled={digits.length !== 4} onClick={() => onSubmit(digits)}>
          Continuar
        </button>
        <button className="kiosk__secondary" onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
